import supportsVenmo from 'braintree-web/venmo/shared/supports-venmo';

import type { ApplePay, ApplePaySession, ApplePayPayload } from 'braintree-web/apple-pay';
import type { BraintreeError, Client as BraintreeClient } from 'braintree-web';
import type { PayPalCheckout } from 'braintree-web/paypal-checkout';
import type { PayPalTokenizePayload } from 'braintree-web/paypal';
import type { Venmo, VenmoPaymentMethodUsage, VenmoTokenizePayload } from 'braintree-web/venmo';

import { BRAINTREE_PAYMENT_TYPES, ROOT_PAYMENT_TYPES, type BraintreePaymentType } from '../constants';
import type SingleCheckoutTypes from '../../../singleCheckout/singleCheckoutTypes';

const IGNORABLE_ERROR_CODES: Set<string> = new Set([
  'Detected popup close',
  'FRAME_SERVICE_FRAME_CLOSED',
  'PAYPAL_POPUP_CLOSED',
  'PAYPAL_START_VAULT_INITIATED_CHECKOUT_CANCELED',
  'VENMO_APP_CANCELED',
  'VENMO_CANCELED',
  'VENMO_CUSTOMER_CANCELED',
  'VENMO_DESKTOP_CANCELED',
  'VENMO_MOBILE_POLLING_TOKENIZATION_CANCELED',
  'Window is closed, can not determine type',
]);

const VENMO_OPTS = {
  allowDesktop: true,
  allowDesktopWebLogin: true,
  // disallow venmo returning to a new browser tab
  // so that we always return to the existing tab
  allowNewBrowserTab: false,
  collectCustomerBillingAddress: true,
  /** multi_use is required for vaulting */
  paymentMethodUsage: 'multi_use' as VenmoPaymentMethodUsage,
};

type PaypalButtonOptions = Parameters<typeof window.paypal.Buttons>[0];

/**
 * Handles configuring and setting up Braintree-related payment sdks and clients
 */
class BraintreeConfigurator {
  /** this aligns with ios 15, our minimum supported version */
  static ApplePayVersion = 12;

  #braintreeClient: BraintreeClient;
  #braintreeClientToken: string;

  configuredTypes: BraintreePaymentType[] = [];

  applePayEnabled = false;
  paypalEnabled = false;
  venmoEnabled = false;

  applePayClient: ApplePay;
  paypalClient: PayPalCheckout;
  venmoClient: Venmo;

  constructor({
    desiredTypes,
    paymentMethodTypes,
  }: {
    /** specific types to enable, or all types if null */
    desiredTypes?: BraintreePaymentType[];
    /** paymentMethodTypes from the pay-config endpoint */
    paymentMethodTypes?: SingleCheckoutTypes.PaymentOptions;
  }) {
    const braintreeConfig = paymentMethodTypes?.[BRAINTREE_PAYMENT_TYPES.PAYPAL];
    const clientToken = braintreeConfig?.clientToken;

    if (!braintreeConfig) {
      return;
    }

    const isMobileApp = window.Exp?.device?.isMobileApp;

    // this is more like "apple pay might be possible" but we need to load the sdk first to be sure
    const applePayEnabled =
      braintreeConfig.paymentTypes.includes(BRAINTREE_PAYMENT_TYPES.APPLE_PAY) &&
      window.isSecureContext;

    const paypalEnabled = braintreeConfig.paymentTypes.includes(BRAINTREE_PAYMENT_TYPES.PAYPAL);

    const venmoEnabled =
      braintreeConfig.paymentTypes.includes(BRAINTREE_PAYMENT_TYPES.VENMO) &&
      supportsVenmo.isBrowserSupported(VENMO_OPTS) &&
      !isMobileApp;

    const availableTypes = ([
      [applePayEnabled, BRAINTREE_PAYMENT_TYPES.APPLE_PAY],
      [paypalEnabled, BRAINTREE_PAYMENT_TYPES.PAYPAL],
      [venmoEnabled, BRAINTREE_PAYMENT_TYPES.VENMO],
    ] as [boolean, BraintreePaymentType][]).map(([enabled, type]) => (
      enabled && (!desiredTypes || desiredTypes.includes(type)) ? type : null
    )).filter(Boolean);

    this.#braintreeClientToken = clientToken;
    this.configuredTypes = availableTypes;

    this.applePayEnabled = availableTypes.includes(BRAINTREE_PAYMENT_TYPES.APPLE_PAY);
    this.paypalEnabled = availableTypes.includes(BRAINTREE_PAYMENT_TYPES.PAYPAL);
    this.venmoEnabled = availableTypes.includes(BRAINTREE_PAYMENT_TYPES.VENMO);
  }

  /**
   * Loads and initializes the Braintree client and all enabled payment methods
   */
  async setup() {
    if (this.configuredTypes.length) {
      const BraintreeClient = await import(/* webpackChunkName: 'braintree-client' */ 'braintree-web/client');
      const client = await BraintreeClient.create({ authorization: this.#braintreeClientToken });

      this.#braintreeClient = client;

      const clientPromises: Promise<void>[] = [];

      if (this.applePayEnabled) {
        clientPromises.push((async () => {
          const BraintreeApplePay = await import(/* webpackChunkName: 'braintree-apple' */ 'braintree-web/apple-pay');
          const applePayClient = await BraintreeApplePay.create({ client });

          this.applePayClient = applePayClient;

          // this has to wait until after the applePayClient is ready
          await this.installApplePayButton();

          let enabled = false;

          try {
            enabled =
              window.ApplePaySession?.supportsVersion(BraintreeConfigurator.ApplePayVersion) &&
              window.ApplePaySession?.canMakePayments();
          } catch {}

          if (!enabled) {
            this.applePayEnabled = false;
            this.configuredTypes = this.configuredTypes.filter((type) => type !== BRAINTREE_PAYMENT_TYPES.APPLE_PAY);

            this.applePayClient.teardown();
            this.applePayClient = null;
          }
        })());
      }

      if (this.paypalEnabled) {
        clientPromises.push((async () => {
          const BraintreePaypalCheckout = await import(/* webpackChunkName: 'braintree-paypal' */ 'braintree-web/paypal-checkout');
          const paypalClient = await BraintreePaypalCheckout.create({ client });

          this.paypalClient = paypalClient;
        })());
      }

      if (this.venmoEnabled) {
        clientPromises.push((async () => {
          const BraintreeVenmo = await import(/* webpackChunkName: 'braintree-venmo' */ 'braintree-web/venmo');
          const venmoClient = await BraintreeVenmo.create({ ...VENMO_OPTS, client });

          this.venmoClient = venmoClient;
        })());
      }

      await Promise.all(clientPromises);
    }
  }

  async installApplePayButton() {
    if (this.applePayClient && !window.customElements?.get('apple-pay-button')) {
      return new Promise((resolve) => {
        const script = document.createElement('script');

        script.src = 'https://applepay.cdn-apple.com/jsapi/1.latest/apple-pay-sdk.js';
        script.async = true;
        script.crossOrigin = 'anonymous';
        script.onload = resolve;
        script.onerror = resolve;

        document.head.appendChild(script);
      });
    }

    return null;
  }

  async installPaypalButton(
    buttonEl: HTMLElement,
    buttonOpts: PaypalButtonOptions,
    currencyCode: SingleCheckoutTypes.CurrencyCode,
    allowPPCredit: boolean,
  ) {
    if (this.#braintreeClient && this.paypalClient) {
      if (!window.paypal?.Buttons) {
        await this.paypalClient.loadPayPalSDK({
          'disable-funding': `card,venmo${allowPPCredit ? '' : ',credit'}`,
          currency: currencyCode,
          // @ts-expect-error @types/braintree-web is missing 'order'
          intent: 'order' as paypal.Intent,
        });
      }

      window
        .paypal
        .Buttons(buttonOpts)
        // @ts-ignore paypal button render types are wrong
        .render(buttonEl);
    }
  }

  static descriptorIsBraintreeType =
    (descriptor: SingleCheckoutTypes.SelectedPaymentDescriptor) => descriptor?.type === ROOT_PAYMENT_TYPES.PAYPAL;

  static isIgnorableError = (error: BraintreeError) => (IGNORABLE_ERROR_CODES.has(error?.code ?? error?.message));

  static paymentIsBraintreeType =
    (payment: SingleCheckoutTypes.ReviewApiRequestPayment): payment is SingleCheckoutTypes.BraintreePayment => (
      payment?.type === BRAINTREE_PAYMENT_TYPES.PAYPAL
    );

  /**
   * converts a paypal, apple pay, or venmo token into what order-set expects
   * @param token a tokenized payment object received from a paypal, apple pay, or venmo tokenize call
   */
  static tokenToOrderPayment =
    (token: ApplePayPayload | PayPalTokenizePayload | VenmoTokenizePayload): SingleCheckoutTypes.BraintreePayment => ({
      account: 'username' in token.details
        ? (token as VenmoTokenizePayload).details.username
        : 'email' in token.details
          ? (token as PayPalTokenizePayload).details.email
          // @ts-expect-error paymentInstrumentName exists but isnt in the types
          : (token as ApplePayPayload).details.paymentInstrumentName,
      nonce: token.nonce,
      paymentType: token.type,
      type: 'PAYPAL',
    });

  /**
   * Provides handling for an ApplePaySession's onvalidatemerchant event
   */
  validateAppleMerchant(session: ApplePaySession) {
    return (event) => {
      this
        .applePayClient
        .performValidation({
          displayName: 'ExpertVoice',
          validationURL: event.validationURL,
        })
        .then((validationData) => {
          session.completeMerchantValidation(validationData);
        })
        .catch(() => {
          session.abort();
        });
    };
  }

  teardown() {
    this.applePayClient?.teardown();
    this.applePayClient = null;

    this.#braintreeClient?.teardown(undefined);
    this.#braintreeClient = null;

    this.paypalClient?.teardown();
    this.paypalClient = null;

    this.venmoClient?.teardown();
    this.venmoClient = null;
  }
}

export default BraintreeConfigurator;
